import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, IconButton,
  Collapse, Chip, Checkbox, Button, CircularProgress, Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { useSnackbar } from 'notistack';
import * as eqpProgramApi from '../../lib/api/eqpProgram';
import EqpProgramDiffDialog from './EqpProgramDiffDialog';
import { formatBytes, formatMtime, canCompare, orderForDiff } from './eqpProgramFormat';

/**
 * (A) 가공 프로그램 탭 - 장비 FTP에서 수집한 파일 목록(이름/크기/수정일/버전수/실행중)과,
 * 행을 펼치면 버전 목록(다운로드/비교 선택)을 보여준다.
 */
const EqpProgramTab = ({ eqpUuid }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // fileNm
  const [versions, setVersions] = useState({}); // fileNm -> list
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [selected, setSelected] = useState({}); // fileNm -> [verId, verId]
  const [diffTarget, setDiffTarget] = useState(null); // {fileName, fromVerId, toVerId}

  const loadFiles = useCallback(() => {
    if (!eqpUuid) return;
    setLoading(true);
    eqpProgramApi.getProgramFiles(eqpUuid)
      .then((res) => setFiles(eqpProgramApi.extractList(res)))
      .catch(() => enqueueSnackbar('가공 프로그램 목록 조회 실패', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [eqpUuid, enqueueSnackbar]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const toggleExpand = async (fileNm) => {
    if (expanded === fileNm) {
      setExpanded(null);
      return;
    }
    setExpanded(fileNm);
    if (!versions[fileNm]) {
      setVersionsLoading(true);
      try {
        const res = await eqpProgramApi.getProgramVersions(eqpUuid, fileNm);
        setVersions((prev) => ({ ...prev, [fileNm]: eqpProgramApi.extractList(res) }));
      } catch (e) {
        enqueueSnackbar('버전 목록 조회 실패', { variant: 'error' });
      } finally {
        setVersionsLoading(false);
      }
    }
  };

  const toggleSelect = (fileNm, verId) => {
    setSelected((prev) => {
      const cur = prev[fileNm] || [];
      const next = cur.includes(verId) ? cur.filter((v) => v !== verId) : [...cur, verId].slice(-2);
      return { ...prev, [fileNm]: next };
    });
  };

  const handleDownload = async (fileNm, verId) => {
    try {
      const res = await eqpProgramApi.downloadProgramVersion(eqpUuid, fileNm, verId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileNm;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      enqueueSnackbar('다운로드에 실패했습니다.', { variant: 'error' });
    }
  };

  const handleCompare = (fileNm) => {
    const sel = selected[fileNm] || [];
    if (!canCompare(sel)) return;
    const { fromVerId, toVerId } = orderForDiff(sel, versions[fileNm] || []);
    setDiffTarget({ fileName: fileNm, fromVerId, toVerId });
  };

  if (loading) {
    return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  }

  if (files.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        아직 수집된 가공 프로그램 파일이 없습니다. FTP 채널이 설정돼 있고 에이전트가 동작 중인지 확인하세요.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>파일명</TableCell>
              <TableCell>크기</TableCell>
              <TableCell>수정일</TableCell>
              <TableCell align="center">버전</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((f) => (
              <React.Fragment key={f.fileNm}>
                <TableRow hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => toggleExpand(f.fileNm)}>
                      {expanded === f.fileNm ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {f.fileNm}
                      {f.running && <Chip size="small" color="info" label="실행 중" />}
                      {f.deletedYn === 'Y' && <Chip size="small" variant="outlined" label="삭제됨" />}
                    </Box>
                  </TableCell>
                  <TableCell>{formatBytes(f.curSize)}</TableCell>
                  <TableCell>{formatMtime(f.curMtime)}</TableCell>
                  <TableCell align="center">{f.verCnt ?? 0}</TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expanded === f.fileNm} unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                        {versionsLoading && !versions[f.fileNm] ? (
                          <CircularProgress size={18} />
                        ) : (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                버전은 최신순으로 표시됩니다. 두 개를 선택하면 비교할 수 있습니다.
                              </Typography>
                              <Button
                                size="small" variant="outlined" startIcon={<CompareArrowsIcon fontSize="small" />}
                                disabled={!canCompare(selected[f.fileNm])}
                                onClick={() => handleCompare(f.fileNm)}
                              >
                                이전 버전과 비교
                              </Button>
                            </Box>
                            {(versions[f.fileNm] || []).map((v) => (
                              <Box
                                key={v.verId}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexWrap: 'wrap' }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={(selected[f.fileNm] || []).includes(v.verId)}
                                  onChange={() => toggleSelect(f.fileNm, v.verId)}
                                />
                                <Typography variant="body2" sx={{ flex: '1 1 160px' }}>
                                  {formatMtime(v.crtDt)} · {formatBytes(v.size)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                  {v.sha256?.slice(0, 10)}
                                </Typography>
                                <Tooltip title="다운로드">
                                  <IconButton size="small" onClick={() => handleDownload(f.fileNm, v.verId)}>
                                    <DownloadIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ))}
                            {(versions[f.fileNm] || []).length === 0 && (
                              <Typography variant="body2" color="text.secondary">버전이 없습니다.</Typography>
                            )}
                          </>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </Box>

      <EqpProgramDiffDialog
        open={Boolean(diffTarget)}
        eqpUuid={eqpUuid}
        fileName={diffTarget?.fileName}
        fromVerId={diffTarget?.fromVerId}
        toVerId={diffTarget?.toVerId}
        onClose={() => setDiffTarget(null)}
      />
    </Box>
  );
};

export default EqpProgramTab;
